import { ROUTES } from '@/config/routes';
import { tryParseJson } from '@/shared/lib/json';

interface GetNotificationLinkParams {
  type: string;
  details?: string;
  extraDetails?: string;
  userType?: string;
}

const parseExtraDetails = (extraDetails?: string) => {
  if (!extraDetails) return undefined;
  let fixed = extraDetails;
  if (fixed.startsWith('"') && fixed.endsWith('"')) {
    fixed = fixed.substring(1, fixed.length - 1);
  }
  fixed = fixed.replace(/\\(.)/g, '$1');
  try {
    return JSON.parse(fixed);
  } catch (_e1) {
    try {
      let fixedAlt = extraDetails;
      if (fixedAlt.startsWith('"') && fixedAlt.endsWith('"')) {
        fixedAlt = fixedAlt.substring(1, fixedAlt.length - 1);
      }
      return JSON.parse(fixedAlt);
    } catch {
      return undefined;
    }
  }
};

const getNotificationLinkRaw = ({
  type,
  details,
  extraDetails,
  userType,
}: GetNotificationLinkParams): string | undefined => {
  const d = tryParseJson(details ?? '{}');
  const extra = parseExtraDetails(extraDetails);

  const buildFocusedOrderLink = (
    orderId?: string,
    primaryBookingId?: string,
    forceBookingsTabWithoutHighlight = false,
    neverHighlight = false,
  ) => {
    if (!orderId) return ROUTES.AUTHORIZED.ORDERS;
    if (neverHighlight) {
      const params = new URLSearchParams({ notificationFocus: '1', openBookings: '1' });
      return `${ROUTES.AUTHORIZED.ORDERS}/${orderId}?${params.toString()}`;
    }
    const rawBookingIds = Array.isArray(d?.bookingIds) ? d.bookingIds.filter(Boolean) : [];
    const bookingIds = Array.from(
      new Set([primaryBookingId, d?.resourceId, d?.bookingId, ...rawBookingIds].filter(Boolean)),
    ).map(String);
    if (bookingIds.length === 0) {
      if (!forceBookingsTabWithoutHighlight) return `${ROUTES.AUTHORIZED.ORDERS}/${orderId}`;
      const params = new URLSearchParams({ notificationFocus: '1', openBookings: '1' });
      return `${ROUTES.AUTHORIZED.ORDERS}/${orderId}?${params.toString()}`;
    }
    const params = new URLSearchParams({
      highlightBookingId: bookingIds[0],
      highlightBookingIds: bookingIds.join(','),
      notificationFocus: '1',
    });
    return `${ROUTES.AUTHORIZED.ORDERS}/${orderId}?${params.toString()}`;
  };

  const buildFocusedMediaPlanLink = (planId?: string, bookingId?: string) => {
    if (!planId) return ROUTES.AUTHORIZED.MEDIA_PLANS;
    if (!bookingId) return `${ROUTES.AUTHORIZED.MEDIA_PLANS}/${planId}`;
    const params = new URLSearchParams({
      highlightBookingId: bookingId,
      notificationFocus: '1',
    });
    return `${ROUTES.AUTHORIZED.MEDIA_PLANS}/${planId}?${params.toString()}`;
  };

  const buildFocusedJourneyLink = (
    orderNumber: string,
    taskId: unknown,
    fallbackServiceType: string,
    focusJourneyAction: string,
  ) => {
    const encodedOrderNumber = encodeURIComponent(orderNumber);
    const base = taskId
      ? `${ROUTES.AUTHORIZED.CAMPAIGN_OVERVIEW}/${encodedOrderNumber}?focusTaskId=${encodeURIComponent(String(taskId))}`
      : `${ROUTES.AUTHORIZED.CAMPAIGN_OVERVIEW}/${encodedOrderNumber}?focusServiceType=${fallbackServiceType}`;
    return `${base}&openJourneyTracking=1&focusJourneyAction=${focusJourneyAction}`;
  };

  switch (type) {
    case 'ORDER_DELIVERY_STATUS_CHECK': {
      const orderNumber = extra?.orderNumber ?? d?.orderNumber ?? '-';
      const taskId = d?.taskId;
      return taskId
        ? `${ROUTES.AUTHORIZED.CAMPAIGN_OVERVIEW}/${orderNumber}?focusTaskId=${taskId}&notificationFocus=1`
        : `${ROUTES.AUTHORIZED.CAMPAIGN_OVERVIEW}/${orderNumber}?focusServiceType=DECORATION`;
    }

    case 'ORDER_DECORATION_STATUS_CHECK': {
      const orderNumber = extra?.orderNumber ?? d?.orderNumber ?? '-';
      const taskId = extra?.taskId ?? d?.taskId;
      return buildFocusedJourneyLink(orderNumber, taskId, 'DECORATION', 'confirm-decoration');
    }

    case 'ORDER_NEUTRALIZATION_STATUS_CHECK': {
      const orderNumber = extra?.orderNumber ?? d?.orderNumber ?? '-';
      const taskId = extra?.taskId ?? d?.taskId;
      return buildFocusedJourneyLink(
        orderNumber,
        taskId,
        'NEUTRALIZATION',
        'confirm-neutralization',
      );
    }

    case 'ORDER_DECORATION_DAYTIME_IMAGES': {
      const orderNumber = extra?.orderNumber ?? d?.orderNumber ?? '-';
      const orderId = extra?.id ?? extra?.rootObjectId ?? d?.rootObjectId ?? d?.id;
      const bookingId = extra?.bookingId ?? d?.bookingId;
      const taskId = extra?.taskId ?? d?.taskId;
      const encodedOrderNumber = encodeURIComponent(orderNumber);
      if (userType === 'service' || !orderId) {
        const base = taskId
          ? `${ROUTES.AUTHORIZED.CAMPAIGN_OVERVIEW}/${encodedOrderNumber}?focusTaskId=${encodeURIComponent(taskId)}`
          : `${ROUTES.AUTHORIZED.CAMPAIGN_OVERVIEW}/${encodedOrderNumber}?focusServiceType=DECORATION`;
        return `${base}&openJourneyTracking=1&focusJourneyAction=upload-daytime-photos`;
      }
      return `${ROUTES.AUTHORIZED.ORDERS}/${orderId}?${bookingId ? `highlightBookingId=${bookingId}&` : ''}notificationFocus=1&focusServiceType=DECORATION`;
    }

    case 'ORDER_NEUTRALIZATION_SOON': {
      const orderNumber = extra?.orderNumber ?? d?.orderNumber ?? '-';
      const taskId = d?.taskId;
      const bookingId = d?.bookingId ?? d?.resourceId;
      const base = `${ROUTES.AUTHORIZED.CAMPAIGN_OVERVIEW}/${orderNumber}?openJourneyTracking=1&focusJourneyAction=contact-supplier-neutralization`;
      if (taskId && bookingId) {
        return `${base}&focusTaskId=${taskId}&journeyBookingId=${bookingId}&notificationFocus=1`;
      }
      if (taskId) return `${base}&focusTaskId=${taskId}&notificationFocus=1`;
      return base;
    }

    case 'COMPANY_CREATED':
    case 'COMPANY_UPDATED':
    case 'COMPANY_DELETED':
    case 'COMPANY_DOCUMENTS_UPDATED':
    case 'COMPANY_DOCUMENT_DELETED':
      return `${ROUTES.AUTHORIZED.COMPANY_FORM}/${d.id}`;

    case 'CLIENT_CREATED':
    case 'CLIENT_UPDATED':
    case 'CLIENT_DELETED':
      return `${ROUTES.AUTHORIZED.COMPANY_CLIENT_FORM}/${d.id}`;

    case 'LOCATION_CREATED':
    case 'LOCATION_UPDATED':
    case 'LOCATION_DELETED':
    case 'LOCATION_DOCUMENT_DELETED':
    case 'LOCATION_FLOOR_MAPS_UPLOADED':
    case 'LOCATION_FLOOR_MAPS_DELETED':
      return `${ROUTES.AUTHORIZED.COMPANY_LOCATION_FORM}/${d.id}`;

    case 'USER_IMPERSONATED':
    case 'USER_CREATED':
    case 'USER_UPDATED':
    case 'USER_AVATAR_UPDATED':
    case 'USER_DISABLED':
    case 'USER_ENABLED':
      return `${ROUTES.AUTHORIZED.PROFILE_FORM}/${d.id}`;

    case 'REBATE_POLICY_CREATED':
    case 'REBATE_POLICY_UPDATED':
    case 'REBATE_POLICY_DELETED':
      return `${ROUTES.AUTHORIZED.COMPANY_PROFILE}/rebate-policies`;

    case 'SERVICE_CREATED':
    case 'SERVICE_UPDATED':
    case 'SERVICE_DELETED':
      return `${ROUTES.AUTHORIZED.SERVICE_FORM}/${d.id}`;

    case 'SERVICE_VIEW_CREATED':
    case 'SERVICE_VIEW_UPDATED':
    case 'SERVICE_VIEW_DELETED':
      return ROUTES.AUTHORIZED.SERVICE_ORDERS;

    case 'SPACE_CREATED':
    case 'SPACE_UPDATED':
    case 'SPACE_DELETED':
    case 'SPACE_DOCUMENT_DELETED':
    case 'SPACE_DRAFT_CREATED':
    case 'SPACE_DRAFT_UPDATED':
    case 'SPACE_DRAFT_DELETED':
      return `${ROUTES.AUTHORIZED.ADVERTISING_SPACES}/${d.id}`;

    case 'SPACE_DRAFT_OLDER_THAN_30_DAYS':
    case 'LOCATION_DRAFTS_TO_REVIEW':
    case 'SPACE_DRAFTS_TO_REVIEW':
      return ROUTES.AUTHORIZED.ADVERTISING_SPACES;

    case 'ADVERTISING_PACKAGE_DRAFTS_TO_REVIEW':
      return ROUTES.AUTHORIZED.ADVERTISING_PACKAGES;

    case 'SPACE_BOOKING_CREATED':
      return `${ROUTES.AUTHORIZED.ADVERTISING_SPACES}/${d.spaceId}`;

    case 'ADVERTISING_PACKAGE_DRAFT_CREATED':
    case 'ADVERTISING_PACKAGE_DRAFT_UPDATED':
    case 'ADVERTISING_PACKAGE_DRAFT_DELETED':
    case 'ADVERTISING_PACKAGE_CREATED':
    case 'ADVERTISING_PACKAGE_UPDATED':
    case 'ADVERTISING_PACKAGE_DELETED':
      return `${ROUTES.AUTHORIZED.ADVERTISING_PACKAGES}/${d.id}`;

    case 'ORGANIZATION_CREATED':
    case 'ORGANIZATION_UPDATED':
    case 'ORGANIZATION_APPROVED':
    case 'ORGANIZATION_REJECTED':
    case 'ORGANIZATION_TENANTS_UPDATED':
    case 'ORGANIZATION_PAYMENT_TERMS_UPDATED':
    case 'ORGANIZATION_SETTINGS_UPDATED':
    case 'ORGANIZATION_COVER_UPDATED':
    case 'ORGANIZATION_DELETED':
      return `${ROUTES.AUTHORIZED.ADMIN_SIGNUP_REQUESTS}/${d?.organizationId ?? d?.id}`;

    case 'MEDIA_PLAN_CREATED':
    case 'MEDIA_PLAN_UPDATED':
    case 'MEDIA_PLAN_DOCUMENT_DELETED':
    case 'MEDIA_PLAN_KEY_VISUALS_UPDATED':
    case 'MEDIA_PLAN_DELETED':
      return `${ROUTES.AUTHORIZED.MEDIA_PLANS}/${d?.planId}`;

    case 'MEDIA_PLAN_BOOKING_DELETED':
      return buildFocusedMediaPlanLink(d?.planId as string | undefined);

    case 'MEDIA_PLAN_BOOKING_ARTWORK_DELETED':
    case 'MEDIA_PLAN_BOOKING_UPDATED':
    case 'MEDIA_PLAN_BOOKING_ARTWORK_UPDATED':
      return buildFocusedMediaPlanLink(
        d?.planId as string | undefined,
        d?.bookingId as string | undefined,
      );

    case 'ORDER_REQUESTED':
      return d?.id ? `${ROUTES.AUTHORIZED.ORDERS}/${d.id}` : ROUTES.AUTHORIZED.ORDERS;

    case 'ADVERTISER_ORDERS_PENDING':
      return ROUTES.AUTHORIZED.ORDERS;

    case 'ORDER_PARTIALLY_REJECTED':
    case 'ORDER_BOOKING_REJECTED':
      return d?.resourceId
        ? `${ROUTES.AUTHORIZED.ORDERS}/${d?.id}?highlightBookingId=${d?.resourceId}&notificationFocus=1&focusRejected=1`
        : `${ROUTES.AUTHORIZED.ORDERS}/${d?.id}?notificationFocus=1&focusRejected=1`;

    case 'ORDER_REJECTED':
      return `${ROUTES.AUTHORIZED.ORDERS}/${d?.id}`;

    case 'ORDER_PARTIALLY_APPROVED':
      return d?.resourceId
        ? `${ROUTES.AUTHORIZED.ORDERS}/${d?.id}?highlightBookingId=${d?.resourceId}&notificationFocus=1&focusApproved=1`
        : `${ROUTES.AUTHORIZED.ORDERS}/${d?.id}?notificationFocus=1&focusApproved=1`;

    case 'ORDER_BOOKING_EXTENSION_SUBMITTED':
    case 'ORDER_BOOKING_EXTENSION_MODIFIED':
    case 'ORDER_BOOKING_EXTENSION_CANCELLED':
    case 'ORDER_BOOKING_EXTENSION_APPROVED':
    case 'ORDER_BOOKING_EXTENSION_REJECTED':
    case 'ORDER_BOOKING_EXTENSION_FORCE_APPROVED':
      return buildFocusedOrderLink(
        (d?.orderId ?? d?.id) as string | undefined,
        (d?.bookingId ?? d?.packageBookingId) as string | undefined,
      );

    case 'ORDER_BOOKING_ARTWORK_OVERDUE':
    case 'ORDER_CAMPAIGN_ARTWORK_OVERDUE':
    case 'ORDER_CAMPAIGN_LAUNCH_AT_RISK':
      return buildFocusedOrderLink(
        d?.id as string | undefined,
        (d?.resourceId ?? d?.bookingId) as string | undefined,
      );

    case 'ORDER_CAMPAIGN_ARTWORK_RECEIVED':
      return d?.id ? buildFocusedOrderLink(d.id as string, undefined, true) : undefined;

    case 'ORDER_SUPPLIER_ASSIGNMENT_REQUIRED':
      return `${ROUTES.AUTHORIZED.CAMPAIGN_OVERVIEW}/${d?.orderNumber ?? d?.id}?focusSupplierAssignment=1&taskId=${d?.taskId ?? ''}&responsibleId=${d?.responsibleId ?? ''}&serviceType=${d?.serviceType ?? ''}`;

    case 'ORDER_RESPONSIBLE_ASSIGNMENT_REQUIRED': {
      const orderNumber = d?.orderNumber ?? '-';
      return d?.taskId
        ? `${ROUTES.AUTHORIZED.CAMPAIGN_OVERVIEW}/${orderNumber}?focusTaskId=${d.taskId}&notificationFocus=1`
        : `${ROUTES.AUTHORIZED.CAMPAIGN_OVERVIEW}/${orderNumber}`;
    }

    case 'ORDER_LOCATION_RESPONSIBLE_ASSIGNED':
    case 'ORDER_LOCATION_RESPONSIBLE_REASSIGNED':
    case 'ORDER_APPROVED':
    case 'ORDER_CREATED':
    case 'ORDER_UPDATED':
    case 'ORDER_KEY_VISUALS_MISSING':
    case 'ORDER_KEY_VISUALS_DELETED':
    case 'ORDER_BOOKING_APPROVED':
    case 'ORDER_BOOKING_UPDATED':
      return `${ROUTES.AUTHORIZED.ORDERS}/${d?.id}`;

    case 'ORDER_BOOKING_DELETED':
      return d?.id ? buildFocusedOrderLink(d.id as string, undefined, false, true) : undefined;

    case 'ORDER_BOOKING_ARTWORK_DELETED':
    case 'ORDER_BOOKING_REQUESTED':
    case 'ORDER_BOOKING_CREATED':
    case 'ORDER_BOOKING_ARTWORK_UPDATED':
    case 'ORDER_BOOKING_ARTWORK_MISSING':
      return d?.id
        ? buildFocusedOrderLink(
            d.id as string,
            (d?.resourceId ?? d?.bookingId) as string | undefined,
            true,
          )
        : undefined;

    case 'ORDER_BOOKING_START_PREPARING_IMPLEMENTATION_DETAILS':
    case 'ORDER_BOOKING_CAMPAIGN_PHOTOS_UPDATED':
    case 'ORDER_PENDING_BOOKING_REMINDER':
    case 'ORDER_CONFIRMED':
    case 'ORDER_STARTS_TODAY':
    case 'ORDER_ENDS_TODAY':
    case 'ORDER_CANCELLED':
      return `${ROUTES.AUTHORIZED.ORDERS}/${d?.id}`;

    case 'ACCESS_REQUESTED':
      return `${ROUTES.AUTHORIZED.ADMIN_SIGNUP_REQUESTS}/${d?.organizationId}`;

    case 'SERVICE_TASK_CONFIRMED':
    case 'SERVICE_TASK_REJECTED': {
      const orderId = d?.rootObjectId ?? d?.orderId ?? extra?.rootObjectId;
      if (!orderId) return undefined;
      return buildFocusedOrderLink(
        orderId as string,
        (extra?.bookingId ?? d?.bookingId) as string | undefined,
      );
    }

    case 'SERVICE_TASK_REASSIGNED': {
      const orderNumber = extra?.orderNumber ?? '-';
      return `${ROUTES.AUTHORIZED.CAMPAIGN_OVERVIEW}/${orderNumber}?focusTaskId=${d?.id ?? ''}&notificationFocus=1`;
    }

    case 'SERVICE_TASK_ASSIGNED': {
      const orderNumber = extra?.orderNumber ?? '-';
      return `${ROUTES.AUTHORIZED.CAMPAIGN_OVERVIEW}/${orderNumber}?focusTaskId=${extra?.taskId ?? ''}&notificationFocus=1`;
    }

    case 'SHIPMENT_AWB_GENERATED':
    case 'SHIPMENT_AWB_GENERATION_FAILED':
    case 'SHIPMENT_OUT_FOR_DELIVERY':
    case 'SHIPMENT_DELIVERED':
    case 'SHIPMENT_PROBLEM':
    case 'SHIPMENT_NOT_DELIVERED':
    case 'SHIPMENT_CANCELLED':
    case 'SHIPMENT_PICKED_UP':
    case 'SHIPMENT_RETURNED':
    case 'SHIPMENT_COMPENSATED': {
      const orderNumber = extra?.orderNumber ?? d?.orderNumber;
      if (!orderNumber) return undefined;
      const taskId = extra?.taskId ?? d?.taskId;
      return `${ROUTES.AUTHORIZED.CAMPAIGN_OVERVIEW}/${encodeURIComponent(String(orderNumber))}?focusTaskId=${encodeURIComponent(String(taskId ?? ''))}&notificationFocus=1`;
    }

    case 'SERVICE_TASK_RESPONSIBLE_ASSIGNED': {
      const orderNumber = extra?.orderNumber ?? '-';
      return `${ROUTES.AUTHORIZED.CAMPAIGN_OVERVIEW}/${orderNumber}?focusTaskId=${extra?.taskId ?? ''}&notificationFocus=1`;
    }

    case 'SERVICE_TASK_DELIVERY_METHOD_MARKED': {
      const orderNumber = extra?.orderNumber ?? d?.orderNumber ?? '-';
      const taskId = extra?.taskId ?? d?.taskId;
      return buildFocusedJourneyLink(orderNumber, taskId, 'DECORATION', 'mark-delivery-method');
    }

    case 'ORDER_MEMBER_ASSIGNED': {
      const orderNumber = extra?.orderNumber ?? '-';
      return `${ROUTES.AUTHORIZED.CAMPAIGN_OVERVIEW}/${orderNumber}?focusTaskId=${extra?.taskId ?? ''}&notificationFocus=1`;
    }

    case 'ORDER_BOOKING_NEUTRALIZATION_IMPLEMENTED': {
      const orderId = extra?.id ?? d?.rootObjectId ?? d?.id;
      return orderId ? `${ROUTES.AUTHORIZED.ORDERS}/${orderId}` : undefined;
    }

    case 'ORDER_LOCATION_NEUTRALIZATION_SOON': {
      const orderId = extra?.id ?? d?.id;
      if (!orderId) return undefined;
      const bookingId = extra?.bookingId ?? d?.bookingId;
      return bookingId
        ? `${ROUTES.AUTHORIZED.ORDERS}/${orderId}?highlightBookingId=${bookingId}&notificationFocus=1`
        : `${ROUTES.AUTHORIZED.ORDERS}/${orderId}?notificationFocus=1`;
    }

    case 'ORDER_LOCATION_CONFIRM_NEUTRALIZATION_SOON': {
      const orderId = extra?.id ?? d?.id;
      if (!orderId) return undefined;
      const bookingId = extra?.bookingId ?? d?.bookingId;
      return bookingId
        ? `${ROUTES.AUTHORIZED.ORDERS}/${orderId}?highlightBookingId=${bookingId}&notificationFocus=1`
        : `${ROUTES.AUTHORIZED.ORDERS}/${orderId}?notificationFocus=1`;
    }

    case 'ORDER_BOOKING_DECORATION_PHOTOS_UPLOADED': {
      const orderId = extra?.id ?? d?.rootObjectId ?? d?.id;
      if (!orderId) return undefined;
      const bookingId = extra?.bookingId ?? d?.bookingId;
      const objectId = (extra?.uploadedPhotos as { objectId?: string }[] | undefined)?.[0]
        ?.objectId;
      return (
        `${ROUTES.AUTHORIZED.ORDERS}/${orderId}?notificationFocus=1` +
        `&openMonitorPhotos=1` +
        `&monitorCategory=Decoration` +
        (bookingId ? `&highlightBookingId=${bookingId}` : '') +
        (objectId ? `&focusObjectId=${objectId}` : '')
      );
    }

    case 'ORDER_BOOKING_NEUTRALIZATION_DONE': {
      const orderId = extra?.id ?? d?.rootObjectId ?? d?.id;
      if (!orderId) return undefined;
      const bookingId = extra?.bookingId ?? d?.bookingId;
      const objectId = (extra?.uploadedPhotos as { objectId?: string }[] | undefined)?.[0]
        ?.objectId;
      return (
        `${ROUTES.AUTHORIZED.ORDERS}/${orderId}?notificationFocus=1` +
        `&openMonitorPhotos=1` +
        `&monitorCategory=Neutralization` +
        (bookingId ? `&highlightBookingId=${bookingId}` : '') +
        (objectId ? `&focusObjectId=${objectId}` : '')
      );
    }

    case 'ORDER_BOOKING_DECORATION_IMPLEMENTED':
    case 'ORDER_BOOKING_DECORATED':
    case 'ORDER_BOOKING_CAMPAIGN_ENDED': {
      const orderId = extra?.id ?? d?.rootObjectId;
      if (!orderId) return undefined;
      const bookingId = extra?.bookingId ?? d?.bookingId;
      return bookingId
        ? `${ROUTES.AUTHORIZED.ORDERS}/${orderId}?highlightBookingId=${bookingId}&notificationFocus=1`
        : `${ROUTES.AUTHORIZED.ORDERS}/${orderId}?notificationFocus=1`;
    }

    case 'SERVICE_TASK_UPDATED': {
      const orderNumber = d?.orderNumber ?? '-';
      return d?.id
        ? `${ROUTES.AUTHORIZED.CAMPAIGN_OVERVIEW}/${orderNumber}?focusTaskId=${d.id}&notificationFocus=1`
        : `${ROUTES.AUTHORIZED.CAMPAIGN_OVERVIEW}/${orderNumber}`;
    }

    case 'SERVICE_TASK_DATES_CHANGED': {
      const orderNumber = d?.orderNumber ?? '-';
      return d?.taskId
        ? `${ROUTES.AUTHORIZED.CAMPAIGN_OVERVIEW}/${orderNumber}?focusTaskId=${d.taskId}&notificationFocus=1`
        : `${ROUTES.AUTHORIZED.CAMPAIGN_OVERVIEW}/${orderNumber}`;
    }

    case 'ORDER_BOOKING_NEEDS_ATTENTION':
    case 'ORDER_DEPLOYMENT_BOOKING_NEEDS_ATTENTION': {
      const orderNumber = extra?.orderNumber ?? d?.orderNumber ?? '-';
      return `${ROUTES.AUTHORIZED.CAMPAIGN_OVERVIEW}/${orderNumber}`;
    }

    case 'ORDER_VISUAL_RESOURCES_UPLOADED':
      return `${ROUTES.AUTHORIZED.CAMPAIGN_OVERVIEW}/${d?.orderNumber ?? '-'}?clearSelection=1&openVisualResources=1`;

    case 'ORDER_KEY_VISUALS_UPLOADED':
      return `${ROUTES.AUTHORIZED.ORDERS}/${d?.id}?openVisualResources=1`;

    case 'ORDER_READY_FOR_PRINTING': {
      const orderNumber = extra?.orderNumber ?? d?.orderNumber ?? '-';
      const taskId = extra?.taskId ?? d?.taskId ?? d?.serviceTaskId;
      return taskId
        ? `${ROUTES.AUTHORIZED.CAMPAIGN_OVERVIEW}/${orderNumber}?focusTaskId=${taskId}&notificationFocus=1`
        : `${ROUTES.AUTHORIZED.CAMPAIGN_OVERVIEW}/${orderNumber}`;
    }

    case 'ORDER_DTP_REVIEW_STARTED': {
      const orderNumber = d?.orderNumber ?? '-';
      const dtpParams = d?.taskId
        ? `focusTaskId=${d.taskId}&notificationFocus=1`
        : `focusServiceType=PRINT`;
      return `${ROUTES.AUTHORIZED.CAMPAIGN_OVERVIEW}/${orderNumber}?${dtpParams}`;
    }

    case 'ORDER_PRINT_PROOF_ARTWORK_RECEIVED': {
      const orderNumber = d?.orderNumber ?? '-';
      return d?.taskId
        ? `${ROUTES.AUTHORIZED.CAMPAIGN_OVERVIEW}/${orderNumber}?focusTaskId=${d.taskId}&notificationFocus=1`
        : `${ROUTES.AUTHORIZED.CAMPAIGN_OVERVIEW}/${orderNumber}`;
    }

    case 'ORDER_DTP_APPROVED': {
      return d?.bookingId
        ? `${ROUTES.AUTHORIZED.ORDERS}/${d?.id}?highlightBookingId=${d.bookingId}&notificationFocus=1`
        : `${ROUTES.AUTHORIZED.ORDERS}/${d?.id}?notificationFocus=1`;
    }

    case 'ORDER_DECORATION_TASK_IN_DELIVERY': {
      const orderNumber = extra?.orderNumber ?? d?.orderNumber ?? '-';
      const orderId = extra?.rootObjectId ?? extra?.id ?? d?.rootObjectId ?? d?.id;
      const bookingId = extra?.bookingId ?? d?.resourceId ?? d?.bookingId;
      const taskId = extra?.taskId ?? d?.id;
      if (userType === 'service' || !orderId) {
        return `${ROUTES.AUTHORIZED.CAMPAIGN_OVERVIEW}/${orderNumber}${taskId ? `?focusTaskId=${taskId}&notificationFocus=1` : '?focusServiceType=DECORATION'}`;
      }
      return `${ROUTES.AUTHORIZED.ORDERS}/${orderId}?${bookingId ? `highlightBookingId=${bookingId}&` : ''}notificationFocus=1&focusServiceType=DECORATION`;
    }

    case 'ORDER_DECORATION_TASK_UNBLOCKED': {
      const orderNumber = extra?.orderNumber ?? d?.orderNumber ?? '-';
      const taskId = extra?.taskId ?? d?.taskId;
      return taskId
        ? `${ROUTES.AUTHORIZED.CAMPAIGN_OVERVIEW}/${orderNumber}?focusTaskId=${taskId}&notificationFocus=1`
        : `${ROUTES.AUTHORIZED.CAMPAIGN_OVERVIEW}/${orderNumber}?focusServiceType=DECORATION`;
    }

    case 'ORDER_UPDATE_LOCATION': {
      const orderNumber = extra?.orderNumber ?? d?.orderNumber ?? '-';
      return `${ROUTES.AUTHORIZED.CAMPAIGN_OVERVIEW}/${orderNumber}?focusServiceType=DECORATION`;
    }

    case 'ORDER_COMMENT_CREATED': {
      let channel = d?.channel ?? (userType === 'service' ? 'services' : 'advertiser');
      if (userType === 'advertiser' && channel === 'advertiser' && extra?.locationId) {
        channel = `advertiser_${extra.locationId}`;
      }
      const locationParam = extra?.locationId ? `&locationId=${extra.locationId}` : '';
      const commentParam = d?.commentId ? `&highlightCommentId=${d.commentId}` : '';
      if (userType === 'service') {
        return `${ROUTES.AUTHORIZED.CAMPAIGN_OVERVIEW}/${d?.orderNumber}?openChat=1&channel=${channel}${locationParam}${commentParam}`;
      }
      return `${ROUTES.AUTHORIZED.ORDERS}/${d?.id}?notificationFocus=1&section=comments&channel=${channel}${locationParam}${commentParam}`;
    }

    case 'ARTWORK_PREVIEW_UPLOAD_NEEDED': {
      const orderNumber = d?.orderNumber ?? '-';
      const taskId = Array.isArray(extra)
        ? extra.find((item: Record<string, unknown>) => item?.taskId)?.taskId
        : undefined;
      const isLink = Array.isArray(extra)
        ? extra.find((item: Record<string, unknown>) => 'isLink' in item)?.isLink
        : undefined;
      const objectId = Array.isArray(extra)
        ? extra.find((item: Record<string, unknown>) => item?.objectId)?.objectId
        : undefined;
      if (!taskId) return `${ROUTES.AUTHORIZED.CAMPAIGN_OVERVIEW}/${orderNumber}`;
      if (isLink) {
        return `${ROUTES.AUTHORIZED.CAMPAIGN_OVERVIEW}/${orderNumber}?focusTaskId=${taskId}&openArtworkLinks=1&notificationFocus=1`;
      }
      return (
        `${ROUTES.AUTHORIZED.CAMPAIGN_OVERVIEW}/${orderNumber}?focusTaskId=${taskId}&openArtwork=1&notificationFocus=1` +
        (objectId ? `&focusObjectId=${objectId}` : '')
      );
    }

    case 'ARTWORK_PREVIEW_APPROVED':
    case 'ARTWORK_PREVIEW_REJECTED': {
      const orderNumber = d?.orderNumber ?? '-';
      if (userType === 'service') {
        const taskId = Array.isArray(extra)
          ? extra.find((item: Record<string, unknown>) => item?.taskId)?.taskId
          : undefined;
        const attachmentId = Array.isArray(extra)
          ? extra.find((item: Record<string, unknown>) => item?.attachmentId)?.attachmentId
          : undefined;
        const versionId = Array.isArray(extra)
          ? extra.find((item: Record<string, unknown>) => item?.versionId)?.versionId
          : undefined;
        return (
          `${ROUTES.AUTHORIZED.CAMPAIGN_OVERVIEW}/${orderNumber}?openArtworkPreview=1` +
          (taskId ? `&focusTaskId=${taskId}` : '') +
          (attachmentId ? `&focusAttachmentId=${attachmentId}` : '') +
          (versionId ? `&focusVersionId=${versionId}` : '')
        );
      }
      const bookingId = Array.isArray(extra)
        ? extra.find((item: Record<string, unknown>) => item?.bookingId)?.bookingId
        : undefined;
      const attachmentId = Array.isArray(extra)
        ? extra.find((item: Record<string, unknown>) => item?.attachmentId)?.attachmentId
        : undefined;
      const versionId = Array.isArray(extra)
        ? extra.find((item: Record<string, unknown>) => item?.versionId)?.versionId
        : undefined;
      return (
        `${ROUTES.AUTHORIZED.ORDERS}/${d?.id}?notificationFocus=1&openArtworkPreview=1` +
        (bookingId ? `&highlightBookingId=${bookingId}` : '') +
        (attachmentId ? `&focusAttachmentId=${attachmentId}` : '') +
        (versionId ? `&focusVersionId=${versionId}` : '')
      );
    }

    case 'ARTWORK_PREVIEW_UPLOADED': {
      const attachmentId = Array.isArray(extra)
        ? extra.find((item: Record<string, unknown>) => item?.attachmentId)?.attachmentId
        : undefined;
      const versionId = Array.isArray(extra)
        ? extra.find((item: Record<string, unknown>) => item?.versionId)?.versionId
        : undefined;
      if (userType === 'service') {
        return `${ROUTES.AUTHORIZED.CAMPAIGN_OVERVIEW}/${d?.orderNumber ?? '-'}`;
      }
      return (
        `${ROUTES.AUTHORIZED.ORDERS}/${d?.id}?notificationFocus=1&openArtworkPreview=1` +
        (d?.resourceId ? `&highlightBookingId=${d.resourceId}` : '') +
        (attachmentId ? `&focusAttachmentId=${attachmentId}` : '') +
        (versionId ? `&focusVersionId=${versionId}` : '')
      );
    }

    case 'ARTWORK_PREVIEW_COMMENT_ADDED':
      if (userType === 'service') {
        return `${ROUTES.AUTHORIZED.CAMPAIGN_OVERVIEW}/${d?.orderNumber ?? '-'}?openArtworkPreview=1${d?.taskId ? `&focusTaskId=${d.taskId}` : ''}${d?.attachmentId ? `&focusAttachmentId=${d.attachmentId}` : ''}${d?.commentId ? `&artworkCommentId=${d.commentId}` : ''}`;
      }
      return d?.bookingId
        ? `${ROUTES.AUTHORIZED.ORDERS}/${d?.id}?highlightBookingId=${d.bookingId}&notificationFocus=1&openArtworkPreview=1${d?.attachmentId ? `&focusAttachmentId=${d.attachmentId}` : ''}${d?.commentId ? `&artworkCommentId=${d.commentId}` : ''}`
        : `${ROUTES.AUTHORIZED.ORDERS}/${d?.id}?notificationFocus=1&openArtworkPreview=1${d?.attachmentId ? `&focusAttachmentId=${d.attachmentId}` : ''}${d?.commentId ? `&artworkCommentId=${d.commentId}` : ''}`;

    case 'ORDER_SERVICES_TASKS_CREATED': {
      const orderNumber = d?.orderNumber ?? '-';
      return `${ROUTES.AUTHORIZED.CAMPAIGN_OVERVIEW}/${orderNumber}`;
    }

    case 'CAMPAIGN_MONITOR_PHOTOS_UPDATED': {
      const objectId = (extra?.uploadedPhotos as { category?: string; objectId?: string }[])?.[0]
        ?.objectId;
      const rawCategory = (extra?.uploadedPhotos as { category?: string; objectId?: string }[])?.[0]
        ?.category;
      const monitorCategory = rawCategory?.includes('DECORATION') ? 'Decoration' : 'Neutralization';
      if (userType === 'service') {
        const params = new URLSearchParams({
          notificationFocus: '1',
          openMonitorPhotos: '1',
          monitorCategory,
        });
        if (extra?.taskId) params.set('focusTaskId', String(extra.taskId));
        if (objectId) params.set('focusObjectId', objectId);
        return `${ROUTES.AUTHORIZED.CAMPAIGN_OVERVIEW}/${extra?.orderNumber ?? '-'}?${params.toString()}`;
      }
      return (
        `${ROUTES.AUTHORIZED.ORDERS}/${extra?.id}?notificationFocus=1` +
        `&highlightBookingId=${extra?.bookingId}` +
        `&openMonitorPhotos=1` +
        `&monitorCategory=${monitorCategory}` +
        (objectId ? `&focusObjectId=${objectId}` : '')
      );
    }

    case 'ORDER_BOOKING_SIMULATION_UPLOADED': {
      const objectId = (extra?.simulationFileIds as string[] | undefined)?.[0];
      if (userType === 'service') {
        const orderNumber = extra?.orderNumber ?? '-';
        const taskId = extra?.taskId;
        const params = new URLSearchParams({ notificationFocus: '1', openSimulation: '1' });
        if (taskId) params.set('focusTaskId', String(taskId));
        if (objectId) params.set('focusObjectId', objectId);
        return `${ROUTES.AUTHORIZED.CAMPAIGN_OVERVIEW}/${orderNumber}?${params.toString()}`;
      }
      const orderId = extra?.id;
      const bookingId = extra?.bookingId;
      const params = new URLSearchParams({ notificationFocus: '1', openSimulation: '1' });
      if (bookingId) params.set('highlightBookingId', bookingId);
      if (objectId) params.set('focusObjectId', objectId);
      return `${ROUTES.AUTHORIZED.ORDERS}/${orderId}?${params.toString()}`;
    }

    case 'SERVICE_TASK_OUTDOOR_WEATHER_ALERT': {
      const orderNumber = extra?.orderNumber ?? '-';
      const taskId = extra?.taskId;
      return `${ROUTES.AUTHORIZED.CAMPAIGN_OVERVIEW}/${orderNumber}${taskId ? `?focusTaskId=${taskId}&notificationFocus=1` : ''}`;
    }

    case 'CAMPAIGN_MONITOR_PHOTOS_DELETED': {
      const rawCategory = extra?.category as string | undefined;
      const monitorCategory = rawCategory?.includes('DECORATION') ? 'Decoration' : 'Neutralization';
      if (userType === 'service') {
        return `${ROUTES.AUTHORIZED.CAMPAIGN_OVERVIEW}/${extra?.orderNumber ?? '-'}${extra?.taskId ? `?focusTaskId=${extra.taskId}&notificationFocus=1` : ''}`;
      }
      return (
        `${ROUTES.AUTHORIZED.ORDERS}/${extra?.id}?notificationFocus=1` +
        `&highlightBookingId=${extra?.bookingId}` +
        `&openMonitorPhotos=1` +
        `&monitorCategory=${monitorCategory}`
      );
    }

    case 'ADVERTISER_DASHBOARD_TODO_CREATED':
    case 'ADVERTISER_DASHBOARD_TODO_UPDATED':
    case 'ADVERTISER_DASHBOARD_TODO_DELETED':
    case 'SERVICES_DASHBOARD_TODO_CREATED':
    case 'SERVICES_DASHBOARD_TODO_UPDATED':
    case 'SERVICES_DASHBOARD_TODO_DELETED': {
      const taskId = d?.id ?? extra?.id;
      if (!taskId) return ROUTES.AUTHORIZED.LANDING;
      const params = new URLSearchParams({
        focusTaskId: String(taskId),
        notificationFocus: '1',
      });
      return `${ROUTES.AUTHORIZED.LANDING}?${params.toString()}`;
    }
  }
};

const getNotificationLink = (params: GetNotificationLinkParams): string | undefined => {
  const link = getNotificationLinkRaw(params);
  if (link && link.includes('undefined')) return undefined;
  return link;
};

export const getNotificationNavigationState = (
  type: string,
): Record<string, unknown> | undefined => {
  if (
    type === 'SPACE_DRAFT_OLDER_THAN_30_DAYS' ||
    type === 'LOCATION_DRAFTS_TO_REVIEW' ||
    type === 'SPACE_DRAFTS_TO_REVIEW' ||
    type === 'ADVERTISING_PACKAGE_DRAFTS_TO_REVIEW'
  ) {
    return { published: 'false' };
  }
  if (type === 'ADVERTISER_ORDERS_PENDING') {
    return {
      status: ['PENDING'],
      sortModel: [{ field: 'orderAvailability.start', sort: 'desc' }],
    };
  }
  return undefined;
};

export default getNotificationLink;
